<script setup>
import { computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { InfoFilled } from "@element-plus/icons-vue";
import { AttendanceWarningByTUID, AddAttendanceWarningPopup } from "@/api/teachinfo";
import { RecordUserBehavior } from "@/api/classtable";
import { useMessageStore } from "@/stores/message";
import { useUserStore } from "@/stores/user";

const router = useRouter();
const messageStore = useMessageStore();
const userStore = useUserStore();

const dialogVisible = computed({
  get: () => !!messageStore.attendanceWarning?.dialog,
  set: (v) => {
    if (!v) messageStore.closeAttendanceWarning();
  },
});

watch(
  () => messageStore.attendanceWarning,
  (value) => {
    if (value?.dialog && value.CLID) {
      recordUserBehavior(753);
    }
  },
  { deep: true }
);

onMounted(() => {
  AttendanceWarningByTUID()
    .then((res) => {
      if (res?.data?.Body?.ID) {
        messageStore.addAttendanceWarning(res.data.Body);
      }
    })
    .catch(() => {});
});

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  const s = String(date.getSeconds()).padStart(2, "0");
  return `${y}-${m}-${d} ${h}:${minute}:${s}`;
}

function recordUserBehavior(labelId) {
  const warning = messageStore.attendanceWarning || {};
  const data = {
    userId: userStore.tuid,
    startTime: formatDate(new Date()),
    endTime: formatDate(new Date()),
    labelId,
    customData: {
      来源: "tutor",
      报警类型: warning.WarnType,
      CLID: warning.CLID,
    },
  };
  RecordUserBehavior(data).catch(() => {});
}

function toCloseAttendanceWarning(type, labelId) {
  recordUserBehavior(labelId);
  const clid = messageStore.attendanceWarning?.CLID;
  if (!clid) {
    messageStore.closeAttendanceWarning();
    return;
  }
  AddAttendanceWarningPopup(clid, type)
    .then((res) => {
      if (res?.data?.Body?.ID) {
        messageStore.addAttendanceWarning(res.data.Body);
      } else {
        messageStore.closeAttendanceWarning();
      }
      if (type === 3) {
        router.push({ path: "/releaseTime/cancle" }).catch(() => {});
      }
    })
    .catch(() => {
      messageStore.closeAttendanceWarning();
    });
}
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    width="600px"
    align-center
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    class="attendance-warning-dialog"
  >
    <div class="aw-body">
      <el-icon class="aw-info" :size="34"><InfoFilled /></el-icon>
      <p class="aw-text">
        We noticed an issue with your attendance for this class.
        <br />
        Will you be able to attend your next scheduled sessions on time?
      </p>
      <div class="aw-footer">
        <button
          type="button"
          class="tutor-pink-btn aw-btn"
          @click="toCloseAttendanceWarning(3, 754)"
        >
          No, I need to cancel
        </button>
        <button
          type="button"
          class="tutor-blue-btn aw-btn"
          @click="toCloseAttendanceWarning(2, 755)"
        >
          Yes, I will be on time
        </button>
      </div>
    </div>
  </el-dialog>
</template>

<style scoped lang="scss">
.aw-body {
  text-align: center;
  padding: 8px 12px 4px;
}

.aw-info {
  color: #ff9991;
  margin-bottom: 8px;
}

.aw-text {
  margin: 16px 0 28px;
  color: #32397a;
  font-size: 15px;
  line-height: 1.6;
  min-height: 72px;
}

.aw-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 12px;
}

.aw-btn {
  height: 32px !important;
  min-width: 200px !important;
  line-height: 32px !important;
}
</style>

<style lang="scss">
.attendance-warning-dialog.el-dialog {
  border-radius: 8px;
  overflow: hidden;
}

.attendance-warning-dialog .el-dialog__header {
  display: none;
}

.attendance-warning-dialog .el-dialog__body {
  padding: 20px 24px 16px;
}
</style>
